import { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import ButtonNew from 'components/buttonNew/buttonNew';

export interface ExportColumn {
  id: string;
  label: string;
  category?: 'basic' | 'detail' | 'advanced' | 'contact' | 'company';
  defaultSelected?: boolean;
}

interface ExcelExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedColumns: string[]) => void;
  availableColumns: ExportColumn[];
  title?: string;
}

export const ExcelExportModal: React.FC<ExcelExportModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  availableColumns,
  title = 'Welke kolommen wil je mee exporteren?'
}) => {
  // Initialize with default selected columns
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set(
      availableColumns
        .filter(col => col.defaultSelected !== false)
        .map(col => col.id)
    )
  );

  if (!isOpen) return null;

  const handleToggle = (columnId: string) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns(new Set(availableColumns.map(col => col.id)));
  };

  const handleDeselectAll = () => {
    setSelectedColumns(new Set());
  };

  const handleSelectDetailColumns = () => {
    // Select all detail (detailvelden) columns
    const detailColumnIds = detailColumns.map(col => col.id);
    setSelectedColumns(prev => {
      const next = new Set(prev);
      detailColumnIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedColumns));
    onClose();
  };

  // Group columns by category
  const basicColumns = availableColumns.filter(col => col.category === 'basic' || !col.category);
  const detailColumns = availableColumns.filter(col => col.category === 'detail');
  const advancedColumns = availableColumns.filter(col => col.category === 'advanced');
  const contactColumns = availableColumns.filter(col => col.category === 'contact');
  const companyColumns = availableColumns.filter(col => col.category === 'company');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-primary/10">
          <h2 className="text-xl font-semibold text-neutral-primary">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-secondary hover:text-neutral-primary transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={handleSelectAll}
              className="text-sm text-moss-primary hover:underline"
            >
              Alles selecteren
            </button>
            <span className="text-neutral-secondary">|</span>
            <button
              onClick={handleDeselectAll}
              className="text-sm text-brick-primary hover:underline"
            >
              Alles deselecteren
            </button>
            {detailColumns.length > 0 && (
              <>
                <span className="text-neutral-secondary">|</span>
                <button
                  onClick={handleSelectDetailColumns}
                  className="text-sm text-moss-primary hover:underline font-medium"
                >
                  Alle detailvelden selecteren
                </button>
              </>
            )}
          </div>

          <div className="space-y-6">
            {/* Basic Columns */}
            {basicColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-primary mb-3">
                  Basis Informatie
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {basicColumns.map(column => (
                    <label
                      key={column.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-neutral-primary/5 p-2 rounded transition-colors"
                    >
                      <Checkbox
                        checked={selectedColumns.has(column.id)}
                        onCheckedChange={() => handleToggle(column.id)}
                      />
                      <span className="text-sm text-neutral-primary">
                        {column.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Detail Columns */}
            {detailColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-primary mb-3">
                  Gedetailleerde Informatie
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {detailColumns.map(column => (
                    <label
                      key={column.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-neutral-primary/5 p-2 rounded transition-colors"
                    >
                      <Checkbox
                        checked={selectedColumns.has(column.id)}
                        onCheckedChange={() => handleToggle(column.id)}
                      />
                      <span className="text-sm text-neutral-primary">
                        {column.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Advanced Columns */}
            {advancedColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-primary mb-3">
                  Geavanceerde Informatie
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {advancedColumns.map(column => (
                    <label
                      key={column.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-neutral-primary/5 p-2 rounded transition-colors"
                    >
                      <Checkbox
                        checked={selectedColumns.has(column.id)}
                        onCheckedChange={() => handleToggle(column.id)}
                      />
                      <span className="text-sm text-neutral-primary">
                        {column.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Contact (Huurder) Columns */}
            {contactColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-primary mb-3">
                  Huurder Informatie
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {contactColumns.map(column => (
                    <label
                      key={column.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-neutral-primary/5 p-2 rounded transition-colors"
                    >
                      <Checkbox
                        checked={selectedColumns.has(column.id)}
                        onCheckedChange={() => handleToggle(column.id)}
                      />
                      <span className="text-sm text-neutral-primary">
                        {column.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Company (Vennootschap) Columns */}
            {companyColumns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-primary mb-3">
                  Vennootschap Informatie
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {companyColumns.map(column => (
                    <label
                      key={column.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-neutral-primary/5 p-2 rounded transition-colors"
                    >
                      <Checkbox
                        checked={selectedColumns.has(column.id)}
                        onCheckedChange={() => handleToggle(column.id)}
                      />
                      <span className="text-sm text-neutral-primary">
                        {column.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Selection Count */}
          <div className="mt-6 p-3 bg-moss-primary/10 rounded-md">
            <p className="text-sm text-moss-primary">
              {selectedColumns.size} {selectedColumns.size === 1 ? 'kolom' : 'kolommen'} geselecteerd
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-primary/10">
          <ButtonNew
            variant="secondaryNeutral"
            onClick={onClose}
            label="Annuleren"
          />
          <ButtonNew
            variant="primaryMoss"
            onClick={handleConfirm}
            label="Exporteren"
            isDisabled={selectedColumns.size === 0}
          />
        </div>
      </div>
    </div>
  );
};
